import { format, parseISO, isSameDay, isSameYear } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { formatCurrencyAmount } from '@/lib/i18n/currency'
import { tzAbbreviation } from '@/lib/timezones'

/**
 * Timezone-aware helpers. Event date/times are stored as UTC instants; the
 * event's IANA `timezone` (e.g. "Australia/Sydney") tells us the wall-clock
 * time to show. When a timeZone is passed we render the instant *in that zone*
 * so a Sydney show reads "7:00 PM" to every visitor regardless of their own
 * browser/server timezone. When omitted we keep the legacy browser-local
 * behavior so existing call sites are unchanged.
 */
const fmt = (date: Date, formatStr: string, timeZone?: string): string =>
  timeZone ? formatInTimeZone(date, timeZone, formatStr) : format(date, formatStr)

/** Hour/minute of an instant as seen in the given timezone (or browser-local). */
const clockParts = (date: Date, timeZone?: string): { h: number; m: number } => {
  if (timeZone) {
    const [h, m] = formatInTimeZone(date, timeZone, 'H:m').split(':')
    return { h: Number(h), m: Number(m) }
  }
  return { h: date.getHours(), m: date.getMinutes() }
}

const sameDayTz = (a: Date, b: Date, timeZone?: string): boolean =>
  timeZone ? fmt(a, 'yyyy-MM-dd', timeZone) === fmt(b, 'yyyy-MM-dd', timeZone) : isSameDay(a, b)

const sameYearTz = (a: Date, b: Date, timeZone?: string): boolean =>
  timeZone ? fmt(a, 'yyyy', timeZone) === fmt(b, 'yyyy', timeZone) : isSameYear(a, b)

/** Short timezone label (e.g. "AEST", "EST", "PST") for the given instant.
 *  Delegates to the shared resolver so labels are consistent everywhere. */
const tzAbbrev = (date: Date, timeZone: string): string => tzAbbreviation(date, timeZone)

/**
 * Format a money amount. When called with no currency, defaults to USD
 * so legacy call sites continue to work unchanged. Pass the tenant
 * currency (from ThemeProvider on the storefront, from a prop / context
 * in admin views) to render in the right symbol + locale.
 */
export const formatPrice = (amount: number | string, currency: string = 'USD'): string => {
  const num = typeof amount !== 'number' ? parseFloat(String(amount)) || 0 : amount
  return formatCurrencyAmount(num, currency)
}

/* Re-export so storefront components can import the hook from this
   module without a second import line. Defined in a separate file
   because hooks must live in `.tsx`-context-safe files; see
   `./use-format-price.ts` for the actual hook body. */
export { useFormatPrice } from './use-format-price'

export const formatDate = (date: string | Date | null | undefined, formatStr = 'MMM dd, yyyy', timeZone?: string): string => {
  if (!date) return ''
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    return fmt(dateObj, formatStr, timeZone)
  } catch {
    return ''
  }
}

export const formatTime = (time: string | Date | null | undefined, formatStr = 'h:mm a', timeZone?: string): string => {
  if (!time) return ''
  try {
    const timeObj = typeof time === 'string' ? parseISO(time) : time
    return fmt(timeObj, formatStr, timeZone)
  } catch {
    return ''
  }
}

export const formatTimestamp = (timestamp: number | null | undefined, formatStr = 'MMM dd, yyyy h:mm a'): string => {
  if (!timestamp) return ''
  try {
    const date = new Date(timestamp * 1000)
    return format(date, formatStr)
  } catch {
    return ''
  }
}

/**
 * Format a date string, appending the time if the event has a specific
 * (non-midnight) time component. Useful for single-day events where the
 * start time matters.
 */
export const formatDateWithTime = (
  date: string | Date | null | undefined,
  dateFormat = 'MMM dd, yyyy',
  timeFormat = 'h:mm a',
  timeZone?: string,
): string => {
  if (!date) return ''
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    const { h, m } = clockParts(dateObj, timeZone)
    const hasTime = h !== 0 || m !== 0
    if (hasTime) {
      return `${fmt(dateObj, dateFormat, timeZone)} · ${fmt(dateObj, timeFormat, timeZone)}`
    }
    return fmt(dateObj, dateFormat, timeZone)
  } catch {
    return ''
  }
}

/**
 * Format an event's date range dynamically:
 *  - Single day with time:  "Mar 15, 2026 · 7:00 PM"
 *  - Single day no time:    "Mar 15, 2026"
 *  - Multi-day same year:   "Mar 15 - Apr 20, 2026 · 7:00 PM"
 *  - Multi-day cross year:  "Dec 28, 2026 - Jan 5, 2027 · 7:00 PM"
 *  - Long form variant uses full weekday/month names for the hero.
 */
export const formatEventDateRange = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  longForm = false,
  timeZone?: string,
): string => {
  if (!startDate) return ''
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
    const end = endDate ? (typeof endDate === 'string' ? parseISO(endDate) : endDate) : null
    const { h, m } = clockParts(start, timeZone)
    const hasTime = h !== 0 || m !== 0
    // Append a short zone label (e.g. "AEST") when rendering in a specific
    // timezone so a time is unambiguous to visitors in other zones.
    const zoneLabel = hasTime && timeZone ? ` ${tzAbbrev(start, timeZone)}`.trimEnd() : ''
    const timePart = hasTime ? ` · ${fmt(start, 'h:mm a', timeZone)}${zoneLabel}` : ''

    // Single-day event (no end date, or end date is same day)
    if (!end || sameDayTz(start, end, timeZone)) {
      const datePart = longForm
        ? fmt(start, 'EEEE, MMMM d, yyyy', timeZone)
        : fmt(start, 'MMM dd, yyyy', timeZone)
      return `${datePart}${timePart}`
    }

    // Multi-day event
    if (longForm) {
      if (sameYearTz(start, end, timeZone)) {
        return `${fmt(start, 'MMMM d', timeZone)} - ${fmt(end, 'MMMM d, yyyy', timeZone)}${timePart}`
      }
      return `${fmt(start, 'MMMM d, yyyy', timeZone)} - ${fmt(end, 'MMMM d, yyyy', timeZone)}${timePart}`
    }

    if (sameYearTz(start, end, timeZone)) {
      return `${fmt(start, 'MMM dd', timeZone)} - ${fmt(end, 'MMM dd, yyyy', timeZone)}${timePart}`
    }
    return `${fmt(start, 'MMM dd, yyyy', timeZone)} - ${fmt(end, 'MMM dd, yyyy', timeZone)}${timePart}`
  } catch {
    return ''
  }
}

export const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return ''
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

export const truncateText = (text: string | null | undefined, length = 100): string => {
  if (!text || text.length <= length) return text || ''
  return text.substring(0, length) + '...'
}
